import { createContext, PropsWithChildren, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Params } from 'react-router-dom';

import { DateRange, DateTime, TimeRange } from '@cast/shared-types';

import { useCostReportPriceType } from 'features/cost-report/hooks/useCostReportPriceType';
import {
  CostReportPriceType,
  type DataStatus,
} from 'features/cost-report/types/common';
import { getCostReportGranularity } from 'features/cost-report/utils/granularity';
import { useReportCapabilitiesQuery } from 'hooks/queries/cost';
import { useCastwareAgentStatus } from 'hooks/useCastwareAgentStatus';
import { useClusterGpuAvailability } from 'hooks/useClusterGpuAvailability';
import { useDayGranularityDateRange } from 'hooks/useDayGranularityDateRange';
import { useParams } from 'hooks/useParams';
import { DataGranularity } from 'types/cost-report';
import { DateRangeFilter, RangePickerOptions } from '@cast/shared-date-ui';
import { readStorage, writeStorage } from 'utils/storage';

import { useAbility } from '@cast/shared-ability-data-access';
import { defaultFilterPresets, shortRangeFilterPresets } from '../constants';
import { useCostReportIdleCosts } from '../hooks/useCostReportIdleCosts';
import { useCostReportTimeRangeSelection } from '../hooks/useCostReportTimeRangeSelection';
import { CostReportChartType } from '../types/costOverTime';
import { isListPriceDataIncomplete } from '../utils/isListPriceDataIncomplete';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const STORE_KEY_BASE = 'cost-report.cluster';

type CostReportProviderProps = PropsWithChildren<{
  rangePickerProps?: RangePickerOptions;
}>;

type CostReportContextValue = {
  /**
   * Routing properties
   */
  routeProps: Params;

  /**
   * Indicates whether default range filter is applied.
   */
  isDefaultFilter: boolean;

  /**
   * Indicates if capabilities are loading.
   */
  isCapabilitiesLoading: boolean;

  /**
   * Indicates whether GPU exporter status is loading.
   */
  isGpuExporterStatusLoading: boolean;
  /**
   * Indicates whether GPU exporter is installed.
   */
  isGpuExporterInstalled: boolean;

  /**
   * Indicates if GPU data available.
   */
  isGpuAvailable: boolean;

  /**
   * Indicates whether egressd status is loading.
   */
  isEgressdStatusLoading: boolean;

  /**
   * Indicates whether egressd is installed.
   */
  isEgressdInstalled: boolean;

  /**
   * Indicates if network data exists in selected date range.
   */
  hasNetworkData: boolean;

  /**
   * Indicates if gpu data exists in selected date range.
   */
  hasGpuData: boolean;

  /**
   * Indicates if gpu metrics exists in selected date range.
   */
  hasGpuMetrics: boolean;

  /**
   * Indicates if reliability metrics are installed on the cluster.
   */
  hasReliabilityMetrics: boolean;

  /**
   * Indicates if tpu data exists in selected date range.
   */
  hasTpuData: boolean;

  /**
   * Data status per report type (received = being processed, available = ready).
   */
  dataStatus: {
    reporting: DataStatus;
    network: DataStatus;
    gpu: DataStatus;
    tpu: DataStatus;
  };

  /**
   * Refetch function for capabilities.
   */
  refetchCapabilities: () => void;

  /**
   * Error that occurred while fetching capabilities.
   */
  capabilitiesError: unknown;

  /**
   * The adjusted date range to use for querying
   */
  adjustedDateRange: DateRange;
  /**
   * The adjusted date range to use for querying with high granularity.
   * Use it to query data for the reports with 5 mins granularity support.
   * Eventually replaces the adjustedDateRange property.
   */
  granularDateRange: TimeRange<DateTime>;
  /**
   * Granularity of the report data.
   */
  granularity: DataGranularity;
  /**
   * Range picker props
   */
  rangePickerProps: RangePickerOptions;
  /**
   * Change report chart type handler
   */
  changeType: (chartType: CostReportChartType) => void;
  /**
   * Report chart type
   */
  chartType: CostReportChartType;
  /**
   * Selected report date range. Use adjustedDateRange for querying
   */
  currentDateRange: DateRange;
  /**
   * Report timezone
   */
  timezone: string;
  /**
   * Report date range filter
   */
  currentDateRangeFilter?: DateRangeFilter;
  /**
   * Indicates whether kvisor status is loading.
   */
  isKvisorStatusLoading: boolean;

  /**
   * Indicates whether kvisor is installed.
   */
  isKvisorInstalled: boolean;

  /**
   * Current price type.
   */
  priceType: CostReportPriceType;

  /**
   * Indicates whether to use list price for cost calculation.
   */
  useListingPrices: boolean;

  /**
   * Indicates whether the selected date range includes periods before list price collection started.
   */
  hasIncompleteListPriceData: boolean;

  /**
   * Change price type.
   */
  updatePriceType(newPriceType: CostReportPriceType): void;

  /**
   * Indicates if report should include idle costs.
   */
  includeIdleCosts: boolean;

  /**
   * Change if report should include idle costs.
   */
  updateIdleCosts: (newIdleCosts: boolean) => void;
};

export const CostReportContext = createContext<CostReportContextValue>(
  {} as never
);

export const CostReportProvider = ({
  children,
  rangePickerProps = {},
}: CostReportProviderProps) => {
  const displayShortRangePresets = useAbility().can(
    'view',
    'FeatureClusterCostMonitoringShortDateRange'
  );
  const params = useParams();

  const { getRange, timeZone, initialState, preset, from, to } =
    useCostReportTimeRangeSelection();
  const { cluster } = useCluster();
  const _range = getRange();

  const currentDateRange = useMemo<DateRange>(() => {
    let from = dayjs.utc(_range.from);
    const to = dayjs.utc(_range.to);
    const maxDays = rangePickerProps?.maxNumberOfDays;
    // Cost report APIs don't support tz-aware data, so we need
    // to manually pad the "from" date in case it is further away in UTC
    // than it is in the selected timezone. This will primarily happen when the
    // user selects the "Last 3 months" preset.
    if (maxDays && to.diff(from, 'day', true) > maxDays) {
      from = to.subtract(maxDays - 1, 'day');
    }
    return [from, to];
  }, [_range.from, _range.to, rangePickerProps?.maxNumberOfDays]);
  const currentDateRangeFilter = initialState.preset;

  const [chartType, setChartType] = useState<CostReportChartType>(() =>
    readStorage(`${STORE_KEY_BASE}.chart-type`, CostReportChartType.BAR)
  );

  const { includeIdleCosts, updateIdleCosts } = useCostReportIdleCosts();

  const changeType = (chartType: CostReportChartType) => {
    setChartType(chartType);
    writeStorage(`${STORE_KEY_BASE}.chart-type`, chartType);
  };

  const adjustedDateRange = useDayGranularityDateRange({
    currentDateRange,
    timezone: timeZone,
    alignToHour: false,
  });

  const granularity = useMemo<DataGranularity>(
    () => getCostReportGranularity(currentDateRange),
    [currentDateRange]
  );

  const granularDateRange = useMemo<TimeRange<DateTime>>(() => {
    const from = currentDateRange[0].tz(timeZone);
    let to = currentDateRange[1].tz(timeZone);

    // ranges selected from picker and chart are not consistent.
    // subtracting 1 ms is required to make the chart data and ticks consistent for both
    if (to.get('milliseconds') === 0) {
      to = to.subtract(1, 'ms');
    }

    return { from: from.utc(), to: to.utc() };
  }, [currentDateRange, timeZone]);

  const { isGpuAvailable } = useClusterGpuAvailability({
    range: {
      from: adjustedDateRange[0].toISOString(),
      to: adjustedDateRange[1].toISOString(),
    },
  });

  const { priceType, updatePriceType } = useCostReportPriceType();

  const hasIncompleteListPriceData = isListPriceDataIncomplete(
    priceType,
    currentDateRange[0]
  );

  const {
    data: reportCapabilities,
    isLoading,
    refetch,
    error,
  } = useReportCapabilitiesQuery({
    clusterId: cluster.id,
    startTime: adjustedDateRange[0].toISOString(),
    endTime: adjustedDateRange[1].toISOString(),
    enabled: !!cluster.id,
  });

  const { isLoading: isGpuExporterStatusLoading, data: gpuExporterStatus } =
    useCastwareAgentStatus({
      name: 'castai-gpu-metrics-exporter',
    });

  const { isLoading: isEgressdStatusLoading, data: egressdAgentStatus } =
    useCastwareAgentStatus({
      name: 'egressd-exporter',
    });

  const isDefaultFilter = useMemo(() => {
    if (!preset && !from && !to) {
      // we can consider it default if there is no preset AND no date range in the URL
      return true;
    }

    if (preset === initialState.preset) {
      return true;
    }

    return false;
  }, [preset, initialState.preset, from, to]);

  const { isLoading: isKvisorStatusLoading, data: kvisorAgentStatus } =
    useCastwareAgentStatus({
      name: 'kvisor',
    });

  return (
    <CostReportContext.Provider
      value={{
        includeIdleCosts,
        updateIdleCosts,
        changeType,
        chartType,
        currentDateRange,
        timezone: timeZone,
        currentDateRangeFilter,
        routeProps: params,
        isGpuExporterStatusLoading,
        isGpuExporterInstalled: Boolean(
          !isGpuExporterStatusLoading &&
            gpuExporterStatus &&
            !['STATUS_UNSPECIFIED', 'DISCONNECTED', 'ERROR'].includes(
              gpuExporterStatus
            )
        ),
        isGpuAvailable,
        isEgressdStatusLoading,
        isEgressdInstalled: Boolean(
          !isEgressdStatusLoading &&
            egressdAgentStatus &&
            !['STATUS_UNSPECIFIED', 'DISCONNECTED', 'ERROR'].includes(
              egressdAgentStatus
            )
        ),
        isKvisorStatusLoading,
        isKvisorInstalled: Boolean(
          !isKvisorStatusLoading &&
            kvisorAgentStatus &&
            !['STATUS_UNSPECIFIED', 'DISCONNECTED', 'ERROR'].includes(
              kvisorAgentStatus
            )
        ),
        hasNetworkData: reportCapabilities?.networkDataAvailable ?? false,
        hasGpuData: reportCapabilities?.gpuDataAvailable ?? false,
        hasGpuMetrics: reportCapabilities?.gpuMetricsAvailable ?? false,
        hasReliabilityMetrics:
          reportCapabilities?.reliabilityMetricsDataAvailable ?? false,
        hasTpuData: reportCapabilities?.tpuDataAvailable ?? false,
        dataStatus: {
          reporting: {
            received: reportCapabilities?.reportingDataReceived ?? false,
            available: reportCapabilities?.reportingDataAvailable ?? false,
          },
          network: {
            received: reportCapabilities?.networkDataReceived ?? false,
            available: reportCapabilities?.networkDataAvailable ?? false,
          },
          gpu: {
            received: reportCapabilities?.gpuDataReceived ?? false,
            available: reportCapabilities?.gpuDataAvailable ?? false,
          },
          tpu: {
            received: reportCapabilities?.tpuDataReceived ?? false,
            available: reportCapabilities?.tpuDataAvailable ?? false,
          },
        },
        isCapabilitiesLoading: isLoading,
        refetchCapabilities: refetch,
        capabilitiesError: error,
        isDefaultFilter,
        priceType,
        useListingPrices: priceType === CostReportPriceType.LIST,
        hasIncompleteListPriceData,
        updatePriceType,
        rangePickerProps: {
          filterPresets: displayShortRangePresets
            ? shortRangeFilterPresets
            : defaultFilterPresets,
          initialPreset: initialState.preset,
          maxNumberOfDays: displayShortRangePresets ? 1 : undefined,
          ...rangePickerProps,
        },
        adjustedDateRange,
        granularDateRange,
        granularity,
      }}
    >
      {children}
    </CostReportContext.Provider>
  );
};
